'use client';

import { MinusCircle, PlusCircle } from '@/components/_rebrand/Icons';
import ease from '@/constants/ease';
import { motion } from 'framer-motion';
import 'keen-slider/keen-slider.min.css';
import Image from 'next/image';
import { FC, useState } from 'react';
export interface FrontIntelligenceProps {
  aiFeatures: {
    title: React.ReactNode;
    description: string;
  }[]
  aiFeaturesHeader: React.ReactNode;
  sideCustomerOne: React.ReactNode;
  sideCustomerTwo: React.ReactNode;
}

const FrontIntelligence: FC<FrontIntelligenceProps> = ({
  aiFeatures = [],
  aiFeaturesHeader,
  sideCustomerOne,
  sideCustomerTwo,
}
) => {
  const [activeFeatureIndex, setFeatureIndex] = useState(0);

  return (
    <div className="bg-violet-900 text-white px-[16px] md:px-[64px] pb-[48px]">
      <div className="flex">
        <div className="md:pt-[98px] mx-auto max-w-[1384px]">
          <div className="flex flex-wrap border-[1px] border-solid border-violet-800 relative">
            <div className="absolute top-[-17px] left-[-17px] hidden md:block">
              <Image src="https://front.com/webassets/rebrand/ai-plus.svg" width={35} height={35} alt="ai plus" />
            </div>
            <div className="flex flex-wrap md:flex-nowrap bg-center bg-top bg-no-repeat bg-[url('https://front.com/webassets/rebrand/innovation-blur.svg')]">
              <div className="flex items-center justify-center w-full md:w-1/2 px-[16px] md:px-0 md:pt-[94px] md:pb-[94px] md:pl-[114px] md:pr-[125px] border-r-[1px] border-b-[1px] border-solid border-violet-800">
                <Image src="https://front.com/webassets/rebrand/front-intelligence-image.png" width={522} height={450} alt="Front Ingelligence" className="p-[24px]" />
              </div>
              <div className="flex items-center p-[16px] md:p-0 md:pr-[64px] md:pl-[125px] w-full md:w-1/2 border-b-[1px] border-solid border-violet-800">
                <div className="flex flex-wrap items-start">
                  <h2 className="flex text-h1 mb-[16px]">Front Intelligence drives a cycle of continuous innovation</h2>
                  <div className="flex flex-wrap text-p1">
                    <p className="mb-[24px]">Automation, assistance, and insights create a flywheel: more automation means more conversations, which leads to smarter insights and even more ways to optimize your business.</p>
                    <p className="mb-[34px]">Front Intelligence leverages text generation APIs from industry-leading providers like OpenAI, Azure, and Amazon AWS and keeps up with the latest model updates for ongoing innovation.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap md:flex-nowrap">
              <div className="flex w-full md:w-1/2 md:pt-[94px] p-[16px] md:p-0 md:pb-[94px] md:pl-[114px] md:pr-[125px] border-r-[1px] border-b-[1px] border-solid border-violet-800 relative">
                <div className="absolute top-[-17px] right-[-18px] hidden md:block">
                  <Image src="https://front.com/webassets/rebrand/ai-plus.svg" width={35} height={35} alt="ai plus" />
                </div>
                <div className="flex flex-wrap items-start">
                  <h2 className="flex text-h1">What makes Front Intelligence so smart?</h2>
                  <div className="flex flex-wrap mt-[32px]">
                    <div className="flex text-p1 font-500 mb-[12px]">
                      Rich data from all your customer interactions
                    </div>
                    <div className="flex text-p2 text-violet-300">
                      Tens of millions of tickets and conversations are handled every week in Front, providing billions of data points for Front Intelligence.
                    </div>
                  </div>
                  <div className="flex flex-wrap mt-[32px]">
                    <div className="flex text-p1 font-500 mb-[12px]">
                      Unified knowledge sources
                    </div>
                    <div className="flex text-p2 text-violet-300">
                      We bring your help content and past conversation data together to provide more relevant, accurate AI suggestions.
                    </div>
                  </div>
                  <div className="flex flex-wrap mt-[32px]">
                    <div className="flex text-p1 font-500 mb-[12px]">
                      AI you can trust, because you’re in control
                    </div>
                    <div className="flex text-p1 text-violet-300">
                      Select your knowledge sources and determine what requires human review, so you can safely scale automation over time.
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center w-full md:w-1/2 border-b-[1px] border-solid border-violet-800">
                <Image src="https://front.com/webassets/rebrand/smart-ai.svg" width={800} height={755} alt="Front Ingelligence" className="p-0" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap mx-[32px] md:mx-[64px]">
            <div className="flex">
              <div className="flex mt-[64px] md:mt-[120px]">
                <h2 className="text-h1 mb-[54px]">{aiFeaturesHeader}</h2>
              </div>
            </div>
            <div className="flex flex-wrap md:flex-nowrap items-start">
              <div className="flex flex-wrap md:mb-[0] w-full md:w-[70%] md:mr-[140px]">
                {aiFeatures.map((item, index) => (
                  <div key={`ai-features-${index}`} className="flex border-b-[1px] border-solid border-violet-800 w-full items-center justify-between cursor-pointer" onClick={() => { setFeatureIndex(index); }}>
                    <div className="flex text-p1 font-500 mt-[16px] mb-[16px]">
                      <div className="flex flex-wrap">
                        {item.title}
                        {activeFeatureIndex === index && (
                          <motion.div
                            className="text-p2 mt-[10px] font-400 text-violet-300 w-full mr-[8px]"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              duration: 400 / 1000,
                              ease: ease.appear,
                            }}
                          >
                            {item.description}
                          </motion.div>
                        )}
                      </div>
                    </div>
                    <div className="flex">
                      {activeFeatureIndex === index ? <motion.div
                        className="text-p2 mt-[10px] font-400 text-violet-300 w-full"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 400 / 1000,
                          ease: ease.appear,
                        }}
                      >
                        {MinusCircle()}
                      </motion.div> : <motion.div
                        className="text-p2 mt-[10px] font-400 text-violet-300 w-full"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 400 / 1000,
                          ease: ease.appear,
                        }}
                      >
                        {PlusCircle()}
                      </motion.div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap w-[30%] hidden md:block">
                {sideCustomerOne}
                {sideCustomerTwo}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrontIntelligence;
